import { FC, useMemo } from "react";
import { UseFormReturn } from "react-hook-form";
import { MiBuildHouseAlt } from "@miplatform/assets";
import { MiAlert, MiPlaceholderArea, MiSection } from "@miplatform/components";
import {
  IAccountResponse,
  ISuppressedWorkTemplateBase,
} from "@miplatform/types";
import { Box, useTheme } from "@mui/material";
import { ConfigAccordionHeader } from "../../components/ConfigAccordionHeader/ConfigAccordionHeader";
import { IWorkOrderTemplateData } from "../ConfigWorkOrderTemplates.types";
import { ConfigWorkOrderCustomerCard } from "../components/ConfigWorkOrderCustomerCard";

export const WorkOrderCustomerExclusionTabView: FC<
  WorkOrderCustomerExclusionTabViewProps
> = ({
  form,
  extraTabProps: {
    isReadOnly,
    accounts,
    excludedAccounts,
    excludeWorkOrderTemplate,
    reenableWorkOrderTemplate,
  } = {
    isReadOnly: false,
    accounts: [],
    excludedAccounts: [],
    excludeWorkOrderTemplate: () => {},
    reenableWorkOrderTemplate: () => {},
  },
}) => {
  const { getValues, watch } = form;
  const sectionTitle = `${getValues("name")} Duration By Builder`;
  const isNew = !getValues("id");
  const theme = useTheme();

  const steps = watch("steps") ?? [];
  const sortedSteps = useMemo(
    () => [...steps].sort((a, b) => a.priorityOrder - b.priorityOrder),
    [steps]
  );

  return (
    <ConfigAccordionHeader title="Production Work Order Capacity">
      {accounts.length === 0 ? (
        <MiPlaceholderArea
          text="No Accounts Found"
          iconComponent={<img src={MiBuildHouseAlt} alt="House Icon" />}
          containerStyle={{
            backgroundColor: theme.palette.primary.light,
            flex: 1,
            minHeight: 500,
          }}
        />
      ) : (
        <>
          {steps.length === 0 && (
            <MiAlert
              description="To properly see your production capacity please add steps and the total work order duration will be calculated here."
              storageKey="configWorkOrderCustomerExclusionTabAlert"
              sx={{ mb: theme.spacing(2) }}
            />
          )}
          <MiSection headerTitle={sectionTitle} hideShadow>
            <Box display="flex" flexDirection="column" gap={2} width="100%">
              {accounts.map((account) => {
                const excludedWorkOrder =
                  excludedAccounts.find((wo) => wo.accountId === account.id) ??
                  undefined;

                return (
                  <ConfigWorkOrderCustomerCard
                    key={account.id}
                    account={account}
                    steps={sortedSteps}
                    disabled={isNew || isReadOnly}
                    isExcluded={!!excludedWorkOrder}
                    onClickExclude={() => excludeWorkOrderTemplate(account.id)}
                    onClickReenable={() => {
                      if (excludedWorkOrder && reenableWorkOrderTemplate) {
                        reenableWorkOrderTemplate(excludedWorkOrder);
                      }
                    }}
                  />
                );
              })}
            </Box>
          </MiSection>
        </>
      )}
    </ConfigAccordionHeader>
  );
};

interface WorkOrderCustomerExclusionTabViewProps {
  form: UseFormReturn<IWorkOrderTemplateData>;
  extraTabProps?: {
    isReadOnly: boolean;
    accounts: IAccountResponse[];
    excludedAccounts: ISuppressedWorkTemplateBase[];
    excludeWorkOrderTemplate: (accountId: string) => void;
    reenableWorkOrderTemplate: (
      suppressedWorkTemplate: ISuppressedWorkTemplateBase
    ) => void;
  };
}
